/**
 * PHARMAKON — the team login on the screen (X XWP-72; CP §15.3 « Shared equipe@officine_name.com
 * session », §11, §15.21-1; XO-12, XD-222, XD-293…XD-296).
 *
 * The browser mirror of the server's workstation rules — `apps/server/src/config/session-poste-equipe.config.js`
 * (durations, idle delays, label width), `compte-equipe.config.js` (code digits, team password bounds, the
 * XD-222 limitation sentence) and `pin-utilisateur.config.js` (PIN bounds). The server stays the authority
 * (it refuses what is out of its lists); the values below only shape the fields, and the parity test
 * `lib/__tests__/session-equipe-xwp72.test.js` reads the server sources so a change there fails here in the
 * same commit (PK-BUG-15: never a re-typed list).
 *
 * The client timings (`*_MS`) are build-time values of this screen (class « build-time », rules/development
 * §4.2): when the team token is renewed, how often a picked user's session is prolonged while active, how
 * long before the server's deadline the screen goes back to the picker.
 */

/** Mirror of the server rules + this screen's own timings. */
export const SESSION_EQUIPE_WEB = Object.freeze({
  // ── mirror (parity-tested) ────────────────────────────────────────────────
  /** XD-293 — the saved durations the owner chooses from (days). */
  DUREES_JOURS: Object.freeze([1, 7, 30]),
  /** XD-222 — the idle delays after which a picked user returns to the picker (minutes). */
  DELAIS_INACTIVITE_MINUTES: Object.freeze([5, 15, 30]),
  /** XD-222 — the idle delay offered first (the server's default). */
  DELAI_INACTIVITE_DEFAUT: 15,
  /** Width of the workstation label. */
  LIBELLE_MAX: 60,
  /** Digits of the owner's e-mailed code. */
  CODE_CHIFFRES: 6,
  /** How long the owner's code stays valid (minutes; the server's answer `expires_in_s` wins when given). */
  CODE_TTL_MINUTES: 10,
  /** XD-296 — how long the owner's password proof opens the sensitive administration (minutes). */
  PREUVE_MINUTES: 10,
  /** The team password bounds (XD-288). */
  MOT_DE_PASSE_EQUIPE_MIN: 12,
  MOT_DE_PASSE_EQUIPE_MAX: 128,
  /** The shortest PIN (XD-259) — « Valider » is offered from this length. */
  PIN_LONGUEUR_MIN: 4,
  /** The longest PIN the pad accepts (the server's verification width: a PIN of an earlier rule stays typeable). */
  PIN_SAISIE_MAX: 16,

  // ── this screen (XD-322) ─────────────────────────────────────────────────
  /** The saved duration selected first on the « Ce poste » step (CP §15.3 « for example, 7 days »). */
  DUREE_DEFAUT_JOURS: 7,
  /** The team token is renewed when less than this is left (it lives 15 minutes, XD-290). */
  MARGE_JETON_POSTE_MS: 60_000,
  /** A picked user's session is prolonged at most once per step, and only after activity. */
  PAS_PROLONGATION_MS: 60_000,
  /** How often the screen checks activity and the deadline. */
  TIC_SUIVI_MS: 5_000,
  /** The screen goes back to the picker this long before the server's deadline. */
  MARGE_RETOUR_CHOIX_MS: 5_000,
  /** After a 409 « déjà renouvelée », the stored pair is reread this often, at most RELECTURES_MAX times. */
  RELECTURE_MS: 300,
  RELECTURES_MAX: 5,
  /** « Sessions actives » page size (the server's default page). */
  PAGE_SESSIONS: 20,
});

/** The server error codes these screens read (parity-tested against `config/constants.js` ERROR_CODES). */
export const CODES_SESSION_EQUIPE = Object.freeze({
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  ACCOUNT_LOCKED: "ACCOUNT_LOCKED",
  ACCOUNT_INACTIVE: "ACCOUNT_INACTIVE",
  TOKEN_INVALID: "TOKEN_INVALID",
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  CODE_EQUIPE_INVALIDE: "CODE_EQUIPE_INVALIDE",
  CODE_EQUIPE_NON_ENVOYE: "CODE_EQUIPE_NON_ENVOYE",
  JETON_EQUIPE_REFUSE: "JETON_EQUIPE_REFUSE",
  SESSION_POSTE_INVALIDE: "SESSION_POSTE_INVALIDE",
  SESSION_POSTE_DEJA_RENOUVELEE: "SESSION_POSTE_DEJA_RENOUVELEE",
  SESSION_EQUIPE_TERMINEE: "SESSION_EQUIPE_TERMINEE",
  PREUVE_SESSION_EQUIPE_REQUISE: "PREUVE_SESSION_EQUIPE_REQUISE",
  PIN_VERROUILLE: "PIN_VERROUILLE",
  PIN_NON_DEFINI: "PIN_NON_DEFINI",
});

/** Why a listed user cannot be picked (the server's RAISON_NON_SELECTIONNABLE). */
export const RAISONS_NON_SELECTIONNABLE = Object.freeze({
  PIN_NON_DEFINI: "PIN_NON_DEFINI",
  PIN_VERROUILLE: "PIN_VERROUILLE",
});

/** Browser storage of the workstation (localStorage: shared by the windows of this browser). */
export const CLES_SESSION_EQUIPE = Object.freeze({
  /** The workstation: team token, refresh token, session, account, pharmacy. */
  POSTE: "pharmakon_equipe_poste",
  /** The user picked by PIN now (no refresh token: the server's selection). */
  SELECTION: "pharmakon_equipe_selection",
  /** sessionStorage — the sentence the picker shows after a return (idle, ended session). */
  MESSAGE_CHOIX: "pharmakon_equipe_message",
  /** The Web Locks name serialising the team-token renewal across windows. */
  VERROU_RENOUVELLEMENT: "pharmakon-equipe-renouvellement",
});

/** The public routes of the team login (outside the shell, like /login). */
export const ROUTES_SESSION_EQUIPE = Object.freeze({
  LOGIN: "/login",
  CONNEXION: "/connexion-equipe",
  CHOIX: "/choix-utilisateur",
});

/** The steps of « Connexion équipe » (the poste and duration step posts WITH the code — XWP-71). */
export const ETAPES_CONNEXION = Object.freeze({
  IDENTIFIANTS: "identifiants",
  CODE: "code",
  POSTE: "poste",
});

/** Why the picker is shown again (the sentence it reads first). */
export const MOTIFS_RETOUR_CHOIX = Object.freeze({
  CHANGEMENT: "CHANGEMENT",
  INACTIVITE: "INACTIVITE",
  TERMINEE: "TERMINEE",
});

const pluriel = (n, un, plusieurs) => `${n} ${n > 1 ? plusieurs : un}`;

/** French copy of the team login (55+ users: one plain sentence each, the role labels kept). */
export const MESSAGES_SESSION_EQUIPE = Object.freeze({
  /** XD-222 — the server's sentence (`MESSAGES_COMPTE_EQUIPE.LIMITATION`), word for word. */
  limitation:
    "La durée d'enregistrement ne limite pas l'accès au poste de l'officine : aucune restriction par adresse IP, réseau ou position n'est appliquée. Chaque connexion d'équipe demande le code envoyé au propriétaire.",
  erreurReseau: "Le serveur ne répond pas — vérifiez la connexion et réessayez.",
  raisons: Object.freeze({
    PIN_NON_DEFINI: "Aucun PIN défini — le propriétaire peut le définir depuis l'équipe.",
    PIN_VERROUILLE: "PIN verrouillé après plusieurs essais erronés.",
  }),

  // the login entry (LoginPage)
  entree: Object.freeze({
    titre: "Poste partagé de l'officine",
    bouton: "Connexion équipe",
    aide: "Le propriétaire ouvre le poste avec le compte d'équipe et son code ; chacun choisit ensuite son nom et saisit son PIN.",
    posteOuvert: (libelle) => `Ce navigateur est le poste « ${libelle} »`,
    choisir: "Choisir l'utilisateur",
  }),

  // « Connexion équipe »
  connexion: Object.freeze({
    titre: "Connexion équipe",
    sousTitre: "Poste partagé — réservé au propriétaire",
    etapes: Object.freeze({ identifiants: "Compte d'équipe", code: "Code du propriétaire", poste: "Ce poste" }),
    etapeSur: (n, total) => `Étape ${n} sur ${total}`,
    identifiant: "Identifiant de l'équipe",
    identifiantAide: "De la forme equipe@nom-de-l-officine.com — affiché dans Paramètres › Sécurité.",
    motDePasse: "Mot de passe de l'équipe",
    recevoirCode: "Recevoir le code",
    envoiEnCours: "Envoi du code…",
    codeEnvoye: (dest, minutes) => `Un code à usage unique a été envoyé à ${dest}. Il reste valable ${minutes} minutes.`,
    code: "Code reçu par e-mail",
    codeAide: (n) => `${n} chiffres`,
    codeFormat: (n) => `Le code se compose de ${n} chiffres.`,
    continuer: "Continuer",
    libelle: "Nom du poste",
    libelleAide: "Par exemple « Comptoir 1 » : il apparaît dans les sessions actives du propriétaire.",
    libelleRequis: (max) => `Nommez le poste (1 à ${max} caractères).`,
    duree: "Rester connecté pendant",
    delai: "Retour au choix de l'utilisateur après inactivité",
    ouvrir: "Ouvrir le poste",
    ouverture: "Ouverture du poste…",
    retour: "Retour",
    personnelle: "Connexion personnelle",
    codeIncorrect: (n) => `Code incorrect — ${pluriel(n, "essai restant", "essais restants")}.`,
    verrouilleJusqua: (heure) => `Compte d'équipe temporairement verrouillé — réessayez après ${heure}.`,
    touches: "Entrée : continuer · Échap : étape précédente",
  }),

  // the picker
  choix: Object.freeze({
    titre: "Qui utilise le poste ?",
    poste: (libelle) => `Poste « ${libelle} »`,
    jusquAu: (date) => `Session du poste enregistrée jusqu'au ${date}`,
    filtre: "Tapez votre nom",
    liste: "Utilisateurs du poste",
    aucun: "Aucun utilisateur ne correspond à ce nom.",
    vide: "Aucun utilisateur n'est rattaché à ce poste.",
    chargement: "Chargement de l'équipe…",
    touches: "↑ ↓ : choisir · Entrée : saisir le PIN · lettres : filtrer",
    fermer: "Fermer le poste",
    fermerTitre: "Fermer ce poste d'équipe ?",
    fermerMessage:
      "Plus personne ne pourra choisir son nom sur ce navigateur : le propriétaire devra reconnecter le poste avec le compte d'équipe et son code.",
    ferme: "Le poste est fermé.",
    posteTermine: "Session du poste expirée ou fermée — reconnectez le poste avec le compte d'équipe.",
    retours: Object.freeze({
      CHANGEMENT: "Utilisateur changé : choisissez votre nom.",
      INACTIVITE: "Retour au choix de l'utilisateur après inactivité.",
      TERMINEE: "Votre session sur ce poste est terminée : choisissez votre nom et saisissez votre PIN.",
    }),
  }),

  // the PIN pad
  pin: Object.freeze({
    titre: (nom) => `PIN de ${nom}`,
    champ: "PIN personnel",
    aide: "Saisissez votre PIN au clavier ou avec les touches, puis Entrée.",
    effacer: "Effacer",
    valider: "Valider",
    annuler: "Annuler",
    verification: "Vérification…",
    chiffre: (d) => `Chiffre ${d}`,
    verrouilleJusqua: (heure) => `PIN verrouillé après plusieurs essais erronés. Réessayez après ${heure} ou demandez au propriétaire de le redéfinir.`,
    touches: "Chiffres : saisir · Retour arrière : effacer · Entrée : valider · Échap : annuler",
  }),

  // the owner's step-up (XD-296)
  preuve: Object.freeze({
    titre: "Confirmez avec votre mot de passe",
    message: (minutes) =>
      `Session ouverte par PIN sur un poste d'équipe : cette action demande votre propre mot de passe. Il reste valable ${minutes} minutes pour ces actions.`,
    champ: "Votre mot de passe",
    confirmer: "Confirmer",
    annuler: "Annuler",
  }),

  // the user menu
  menu: Object.freeze({
    changer: "Changer d'utilisateur",
    poste: (libelle) => `Poste « ${libelle} »`,
  }),

  // Paramètres › Sécurité
  securite: Object.freeze({
    compteTitre: "Compte d'équipe (poste partagé)",
    compteAide:
      "Le compte d'équipe ouvre un poste partagé : vous seul en connaissez le mot de passe et recevez le code de chaque connexion ; chacun choisit ensuite son nom et saisit son PIN.",
    identifiant: "Identifiant",
    identifiantPropose: "Identifiant attribué à la création",
    etat: "État",
    actif: "Actif",
    desactive: "Désactivé",
    verrouille: (heure) => `Verrouillé jusqu'à ${heure}`,
    derniereConnexion: "Dernière connexion d'équipe",
    motDePasseModifie: "Mot de passe modifié",
    destinataires: "Le code de connexion est envoyé à",
    jamais: "Jamais",
    creer: "Créer le compte d'équipe",
    changer: "Changer le mot de passe",
    desactiver: "Désactiver le compte",
    reactiver: "Réactiver le compte",
    dialogueCreer: "Créer le compte d'équipe",
    dialogueChanger: "Changer le mot de passe du compte d'équipe",
    nouveau: "Nouveau mot de passe de l'équipe",
    nouveauAide: (min) => `Au moins ${min} caractères, une majuscule, une minuscule et un chiffre — différent du vôtre.`,
    confirmation: "Confirmer le mot de passe de l'équipe",
    differents: "Les deux mots de passe saisis ne sont pas identiques.",
    proprietaire: "Votre propre mot de passe",
    proprietaireAide: "Il confirme que c'est bien vous qui modifiez le compte d'équipe.",
    rotationAvertissement: "Changer le mot de passe ferme tous les postes ouverts : chacun devra être reconnecté.",
    desactiverTitre: "Désactiver le compte d'équipe ?",
    desactiverMessage: "Les postes ouverts sont fermés tout de suite et plus aucune connexion d'équipe n'est possible jusqu'à la réactivation.",
    cree: "Compte d'équipe créé.",
    change: "Mot de passe du compte d'équipe changé — les postes ouverts sont fermés.",
    desactiveOk: "Compte d'équipe désactivé.",
    reactiveOk: "Compte d'équipe réactivé.",
    enregistrer: "Enregistrer",
    annuler: "Annuler",

    sessionsTitre: "Sessions actives",
    sessionsAide: "Les postes d'équipe ouverts sur ce compte, avec l'utilisateur choisi en ce moment.",
    filtre: "Afficher",
    etats: Object.freeze({ ACTIVE: "Actives", EXPIREE: "Expirées", REVOQUEE: "Révoquées", TOUTES: "Toutes" }),
    etatLigne: Object.freeze({ ACTIVE: "Active", EXPIREE: "Expirée", REVOQUEE: "Révoquée" }),
    // typographic apostrophes: the sentence is asserted verbatim in rendered markup
    aucune: "Aucun poste d’équipe n’est ouvert.",
    colonnes: Object.freeze({
      poste: "Poste",
      utilisateur: "Utilisateur en cours",
      ouverte: "Ouverte le",
      jusqua: "Jusqu'au",
      activite: "Dernière activité",
      origine: "Navigateur · adresse",
      actions: "Actions",
    }),
    personne: "Personne (choix de l'utilisateur)",
    revoquer: "Révoquer",
    revoquerTout: "Révoquer tous les postes",
    revoquerTitre: (libelle) => `Révoquer le poste « ${libelle} » ?`,
    revoquerMessage: "Le poste revient à la connexion d'équipe : l'utilisateur en cours est déconnecté à sa prochaine action.",
    revoquerToutTitre: "Révoquer tous les postes d'équipe ?",
    revoquerToutMessage: "Chaque poste ouvert revient à la connexion d'équipe ; les utilisateurs en cours sont déconnectés à leur prochaine action.",
    revoque: (libelle) => `Poste « ${libelle} » révoqué.`,
    revoques: (n) => `${pluriel(n, "poste révoqué", "postes révoqués")}.`,
    precedent: "Page précédente",
    suivant: "Page suivante",
    page: (p, n) => `Page ${p} sur ${n}`,
  }),
});
